// 드래그 의미론 순수함수 — 일(day) 단위 (Design §5.2 결정 7~11).
// 불변: 시간 계산은 scale.ts 의 정수 day-index 를 재사용한다. 날짜 산술을 여기서 다시 구현하지 않는다 (결정1).
// 불변: 2단 분해 — resolveDrag(px) → resolveSpan(일). 키보드가 같은 함수를 타므로 테이블 1개로 마우스+키보드를 동시에 잠근다 (결정7).
// 불변: resolveDrag 는 (base, 총dx)의 함수. 증분 누적 금지 — 드리프트를 구조적으로 제거한다 (결정8).
// 불변: 최소 minDays(기본 1)일, 뒤집기 없음 — DB CHECK 와 동일 문장. resize 로 start > end 가 되려 하면 최소기간에서 멈춘다 (결정9).
// 불변: **절대 throw 하지 않는다** — 드래그 중 예외 = 포인터 캡처 고아 = UI 잠김 (결정11). scale.toDayIndex 는 throw 하므로 전부 감싼다.
// 테스트 잠금(drag.test.ts): move 는 duration 보존(경계에서도 자르지 않고 밀기 — 결정10) · resize 는 해당 끝만 클램프 ·
//   마일스톤(end null)은 move 만 유효 · resize-end −100일 = 뒤집기 대신 최소 1일 · NaN/Infinity/뒤집힌 base = 무변화.
import { fromDayIndex, pxToDays, toDayIndex, type Scale } from "./scale";

/** 드래그 모드 — 바 전체 이동 / 좌단 리사이즈 / 우단 리사이즈. */
export type DragMode = "move" | "resize-start" | "resize-end";

/** 기간. end === null = 마일스톤(끝이 없다 — DDL CHECK 와 동일). */
export interface Span {
  start: string;
  end: string | null;
}

/** 제약. minDays 기본 1. bound* 는 스케일/계약 경계 클램프용(생략 = 무제한). */
export interface SpanOpts {
  minDays?: number;
  boundStart?: string;
  boundEnd?: string;
}

/** base 의 값 사본. 무변화 케이스도 새 객체를 반환한다 — 호출자가 base 를 mutate 당하지 않는다. */
function copy(base: Span): Span {
  return { start: base?.start as string, end: base?.end ?? null };
}

/**
 * "YYYY-MM-DD" → day-index, 실패 시 null.
 * scale.toDayIndex 는 잘못된 날짜에 throw 한다(무성한 NaN 기하 방지 — 옳다).
 * 그러나 드래그 경로는 throw 가 금지(결정11)라 여기서 null 로 강등한다.
 */
function dayOrNull(iso: string | null | undefined): number | null {
  if (typeof iso !== "string") return null;
  try {
    return toDayIndex(iso);
  } catch {
    return null;
  }
}

/** lo/hi 로 클램프. null = 그 방향 무제한. lo > hi 인 모순 설정에서는 lo 가 이긴다. */
function clamp(n: number, lo: number | null, hi: number | null): number {
  if (lo !== null && n < lo) return lo;
  if (hi !== null && n > hi) return hi;
  return n;
}

/**
 * 드래그/키보드 이동의 의미론 — 일(day) 단위 순수함수 (Design §5.2 결정7).
 *
 * - `move`: start/end 동시 이동. **duration 보존**. 경계에서는 자르지 않고 **밀어 붙인다**(결정10).
 * - `resize-start` / `resize-end`: 해당 끝만 이동, 반대쪽 고정. 경계는 그 끝만 클램프.
 * - 최소 `minDays`(기본 1)일. 뒤집으려 하면 최소기간에서 멈춘다 — **뒤집기 없음**(결정9, DB CHECK 와 동일 문장).
 * - 마일스톤(`end === null`)은 `move` 만 유효. `resize-*` 는 무변화.
 * - **절대 throw 하지 않는다**(결정11). 이상 입력(NaN/Infinity/잘못된 ISO/뒤집힌 base)은 base 무변화로 방어한다.
 * - 항상 새 객체를 반환하며 base 를 mutate 하지 않는다.
 */
export function resolveSpan(mode: DragMode, base: Span, deltaDays: number, opts?: SpanOpts): Span {
  try {
    return resolve(mode, base, deltaDays, opts);
  } catch {
    return copy(base); // 최후 방어망 — 어떤 경로로도 드래그 중 예외를 밖으로 내보내지 않는다
  }
}

/** resolveSpan 의 본체. 바깥 try/catch 가 있으므로 여기서는 정상 경로만 읽으면 된다. */
function resolve(mode: DragMode, base: Span, deltaDays: number, opts?: SpanOpts): Span {
  const s0 = dayOrNull(base?.start);
  if (s0 === null) return copy(base); // 시작일이 없거나 깨졌다 → 손대지 않는다

  const isMilestone = base.end === null || base.end === undefined;
  const e0 = isMilestone ? null : dayOrNull(base.end);
  if (!isMilestone && e0 === null) return copy(base); // 바인데 종료일이 깨졌다
  if (e0 !== null && e0 < s0) return copy(base); // 이미 뒤집힌 base — 조용히 고치지 않는다(§5.2 결정9)

  if (!Number.isFinite(deltaDays)) return copy(base); // NaN / ±Infinity
  const d = Math.round(deltaDays);

  const minDays =
    opts && Number.isFinite(opts.minDays as number) ? Math.max(1, Math.round(opts.minDays as number)) : 1;
  const bs = dayOrNull(opts?.boundStart);
  const be = dayOrNull(opts?.boundEnd);

  // 마일스톤 — move 만 유효. resize 핸들 자체가 렌더되지 않지만 키보드 경로가 있으므로 여기서도 막는다.
  if (e0 === null) {
    if (mode !== "move") return copy(base);
    return { start: fromDayIndex(clamp(s0 + d, bs, be)), end: null };
  }

  if (mode === "move") {
    // duration 은 불가침이다 — 경계에서 자르면 조용한 데이터 파괴가 된다(결정10).
    // 끝 경계 → 시작 경계 순으로 밀어서, 스팬이 경계보다 넓은 모순 상황에서는 시작이 이긴다.
    const dur = e0 - s0;
    let ns = s0 + d;
    let ne = e0 + d;
    if (be !== null && ne > be) {
      ne = be;
      ns = be - dur;
    }
    if (bs !== null && ns < bs) {
      ns = bs;
      ne = bs + dur;
    }
    return { start: fromDayIndex(ns), end: fromDayIndex(ne) };
  }

  if (mode === "resize-start") {
    // 경계를 먼저, 최소기간을 나중에 → 뒤집기 금지가 경계보다 강하다(DB CHECK 와 동일 문장).
    let ns = clamp(s0 + d, bs, be);
    const maxStart = e0 - (minDays - 1);
    if (ns > maxStart) ns = maxStart;
    return { start: fromDayIndex(ns), end: fromDayIndex(e0) };
  }

  if (mode === "resize-end") {
    let ne = clamp(e0 + d, bs, be);
    const minEnd = s0 + (minDays - 1);
    if (ne < minEnd) ne = minEnd;
    return { start: fromDayIndex(s0), end: fromDayIndex(ne) };
  }

  return copy(base); // 미지의 mode — 조용히 무변화
}

/**
 * 픽셀 래퍼 — dx 를 일수로 환산해 resolveSpan 에 위임한다 (Design §5.2 결정8).
 * **(base, 총dx)의 함수다.** 포인터다운 시점의 base 와 그때부터의 누적 dx 를 넘겨라.
 * 직전 프레임 결과에 증분을 더하면 반올림 오차가 누적된다 — 그 구조를 원천 차단한다.
 * pxToDays 가 widthPx ≤ 0 / NaN / Infinity 를 이미 0 으로 방어하므로 여기서도 throw 하지 않는다(결정11).
 */
export function resolveDrag(
  mode: DragMode,
  base: Span,
  dx: number,
  widthPx: number,
  s: Scale,
  opts?: SpanOpts,
): Span {
  return resolveSpan(mode, base, pxToDays(dx, widthPx, s), opts);
}
